import _ from 'lodash';
import { DateTime } from 'luxon';

import {
  date as commonDate,
  error as commonError,
  finleyAssert,
  url as commonUrl,
  utils as commonUtils,
  debugLog,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  BankAccountWithInstitutionAndDesignationGQLType,
  DeliverablesPayloadFragmentGQLType,
  EventForRenderFragmentGQLType,
  FundingRequestComponentPayloadFragmentGQLType,
  FundingRequestPayloadFragmentGQLType,
  IngestionPayloadFragmentGQLType,
  InsertedDeliverablePayloadFragmentGQLType,
  Maybe,
  SubmittedDeliverableSubmissionPayloadFragmentGQLType,
  UpdatedDeliverableStatusPayloadFragmentGQLType,
  VerificationPayloadFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { Bank } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const { isNil, isNumber, round } = _;

// TODO: All of these methods should be consoildates with common-modules

/**
 * Convert a number to one with commas, e.g. 12345 => 12,345
 * @param number number, can be either string or number
 */
export const numberWithCommas = (number?: string | number) => {
  if (isNil(number)) {
    return number;
  }
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Convert a number to one with commas, e.g. 12345 => $12,345
 * @param number number, can be either string or number
 */

// TODO: make it generic for other currencies and get rid of mxnNumberWithCommas.
export const dollarNumberWithCommas = (
  number?: string | number,
  fixedPoint: number = 2,
) => {
  if (isNil(number) || !isNumber(number)) {
    return number;
  }
  const dollarVal = round(Math.abs(Number(number)), 2).toFixed(fixedPoint);
  if (number === 0) {
    return '$0';
  }
  if (number < 0) {
    return `-$${numberWithCommas(dollarVal)}`;
  }
  return `$${numberWithCommas(dollarVal)}`;
};

const mxnNumberWithCommas = (
  number?: string | number,
  fixedPoint: number = 2,
) => {
  if (isNil(number) || !isNumber(number)) {
    return number;
  }
  const dollarVal = round(Math.abs(Number(number)), 2).toFixed(fixedPoint);
  if (number === 0) {
    return 'MX $0';
  }
  if (number < 0) {
    return `-MX $${numberWithCommas(dollarVal)}`;
  }
  return `MX $${numberWithCommas(dollarVal)}`;
};

/**
 * Converts a number to one with dollar and commas, and displays 0 as '-'
 * Unlike `dollarNumberWithCommas` which returns 0 as $0
 */
export const dollarNumberWithCommasOrDash = (value: number) =>
  value === 0 ? '-' : dollarNumberWithCommas(value);

/**
 * Convert a number to one that's formatted, e.g. 20000 => 20K and 5000000 to 5M
 * @param number number to be formatted
 */
export const numberToFormattedRounded = (number?: number): Maybe<string> => {
  if (isNil(number)) {
    return null;
  }
  const absNumber = Math.abs(number);
  let formattedNumber = `${Number(absNumber.toFixed(2)) * 1}`; // multiplying by 1 to remove trailing zeroes
  if (absNumber >= 1000000000) {
    formattedNumber = `${(absNumber / 1000000000)
      .toFixed(1)
      .replace(/\.0$/, '')}B`;
  } else if (absNumber >= 1000000) {
    formattedNumber = `${(absNumber / 1000000)
      .toFixed(1)
      .replace(/\.0$/, '')}M`;
  } else if (absNumber >= 1000) {
    formattedNumber = `${(absNumber / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  if (number < 0) {
    return `-${formattedNumber}`;
  }
  return formattedNumber;
};

/**
 * Convert a number to one that's formatted, e.g. 20000 => $20K and 5000000 to $5M
 * @param number number to be formatted
 */
export const numberToDollarFormattedRounded = (
  number?: number,
): Maybe<string> => {
  const formattedRoundedNumber = numberToFormattedRounded(number);
  if (isNil(formattedRoundedNumber)) {
    return null;
  }
  if (formattedRoundedNumber.slice(0, 1) === '-') {
    return `-$${formattedRoundedNumber.slice(1)}`;
  }
  return `$${formattedRoundedNumber}`;
};

/**
 * Converts a number below a given threshold value to 0
 */
export const numberToZeroBelowThreshold = ({
  num,
  threshold = 0.0000001, // 1e-7, where js automatically converts to scientific notation,
}: {
  num: number;
  threshold?: number;
}): number => {
  return num < threshold ? 0 : num;
};

/**
 * Convert a number to percentage label. e.g. 0.0124 => "1.24%"
 * @param number decimal number
 * @param scale the number of digits past the decimal point
 * @returns percentage value string
 */
export const numberToPercentageLabel = (
  number: Maybe<number>,
  scale = 3,
): Maybe<string> => {
  if (isNil(number)) {
    return null;
  }
  return `${round((number as number) * 100, scale)}%`;
};

/**
 * Convert a number to percentage label. e.g. 1.24 => "1.24%"
 * @param number decimal number
 * @param scale the number of digits past the decimal point
 * @returns percentage value string
 */
export const percentageToPercentageLabel = (
  number: Maybe<number>,
  scale = 3,
  fixDecimals?: boolean,
): Maybe<string> => {
  if (isNil(number)) {
    return null;
  }
  const roundedPercentage = round(number as number, scale);
  if (fixDecimals) {
    return `${roundedPercentage.toFixed(scale)}%`;
  }
  return `${roundedPercentage}%`;
};

/**
 * Converts BankAccountWithInstutitionGQLType data to displayable string.
 */
export const bankAccountToDisplayString = (
  bankAccount?: BankAccountWithInstitutionAndDesignationGQLType & {
    organizationName?: string;
  },
): string => {
  const DEFAULT_BANK_STRING = '';
  if (!bankAccount) {
    return DEFAULT_BANK_STRING;
  }
  return `${
    bankAccount.organizationName ? `${bankAccount.organizationName}: ` : ''
  } ${bankAccount.institutionName} (${bankAccount.accountNumber})`;
};

// syntactic sugar to guarantee payload is of type FundingRequest
export const getDeliverablesPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): DeliverablesPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'DeliverablesPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type DeliverablesPayload',
      typeguardName: 'withDeliverablesPayloadAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

export const getUpdatedDeliverableStatusPayloadV1WithAssert = (
  event: EventForRenderFragmentGQLType,
): UpdatedDeliverableStatusPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'UpdatedDeliverableStatusPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type UpdatedDeliverableStatusPayload',
      typeguardName: 'getUpdatedDeliverableStatusPayloadV1WithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

export const getInsertedDeliverableStatusPayloadV1WithAssert = (
  event: EventForRenderFragmentGQLType,
): InsertedDeliverablePayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'InsertedDeliverablePayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type InsertedDeliverablePayload',
      typeguardName: 'getInsertedDeliverableStatusPayloadV1WithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

export const getSubmittedDeliverableSubmissionPayloadV1WithAssert = (
  event: EventForRenderFragmentGQLType,
): SubmittedDeliverableSubmissionPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'SubmittedDeliverableSubmissionPayload',
    new commonError.TypeError({
      resourceType:
        'Payload is not of type SubmittedDeliverableSubmissionPayload',
      typeguardName: 'getSubmittedDeliverableStatusPayloadV1WithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

// syntactic sugar to guarantee payload is of type FundingRequest
export const getFundingRequestPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): FundingRequestPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'FundingRequestPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type FundingRequestPayload',
      typeguardName: 'withFundingRequestPayloadAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

// syntactic sugar to guarantee payload is of type FundingRequestComponent
export const getFundingRequestComponentPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): FundingRequestComponentPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'FundingRequestComponentPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type FundingRequestComponentPayload',
      typeguardName: 'withFundingRequestComponentPayloadAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

// syntactic sugar to guarantee payload is of type IngestionPayload
export const getIngestionPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): IngestionPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'IngestionPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type IngeestionPayload',
      typeguardName: 'getFundingRequestPayloadWithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

// syntactic sugar to guarantee payload is of type IngestionPayload
export const getVerificationPayloadWithAssert = (
  event: EventForRenderFragmentGQLType,
): VerificationPayloadFragmentGQLType => {
  const { payload } = event;
  finleyAssert(
    payload.__typename === 'VerificationPayload',
    new commonError.TypeError({
      resourceType: 'Payload is not of type VerificationPayload',
      typeguardName: 'getVerificationPayloadWithAssert',
      metadata: {
        logMeta: {
          event,
        },
      },
    }),
  );
  return payload;
};

/**
 * Returns a truncated filename
 */
export const truncateUploadFileName = (
  fileName: string | undefined,
  maxLength: number = 43,
) => {
  if (fileName) {
    const sideLength = Math.floor((maxLength - 3) / 2);
    return fileName.length > maxLength
      ? `${fileName.substring(0, sideLength)}...${fileName.substring(
          fileName.length - sideLength,
        )}`
      : fileName;
  }
  return '';
};

/**
 * Helper to format currency amounts based on currency code
 * @param amount currency amount
 * @param currencyCode currency code from Bank.Currency
 * @returns formatted currency amount
 */
export const formatCurrencyAmount = (
  amount: number,
  currencyCode: `${Bank.Currency}`,
) => {
  switch (currencyCode) {
    case Bank.Currency.MXN:
      return mxnNumberWithCommas(amount);
    case Bank.Currency.USD:
    case Bank.Currency.CAD:
      return dollarNumberWithCommas(amount);
    default:
      throw new Error('Did not register currency code format');
  }
};

export const fileExtensionToMimeTypeMap: Record<string, string> = {
  // office
  doc: 'application/msword',
  docx:
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // data
  csv: 'text/csv',
  json: 'application/json',
  txt: 'text/plain',
  rtf: 'application/rtf',
  // general
  rar: 'application/vnd.rar',
  gzip: 'application/gzip',
  zip: 'application/zip',
  pgp: 'application/pgp-encrypted',
  // images
  png: 'image/png',
  bmp: 'image/bmp',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

export const isCustomerDashboard = () =>
  window.location.origin === commonUrl.getCustomerDashboardUrl();

export const isAdminDashboard = () =>
  window.location.origin === commonUrl.getAdminDashboardUrl();

export const getUnexpectedErrorMessage = (err: Error) => {
  // when FE needs to surface unknown errors to the user.
  // for customer dashboard on prod, return a generic message
  if (commonUtils.EnvUtils.isProduction() && isCustomerDashboard()) {
    // we are not going to show real message to the user
    // but log it for debugging (debugLog can be enabled dynamically)
    debugLog('Debug Error:', err);
    return 'An unexpected error occurred. Please contact Finley for support.';
  }

  // otherwise do not hide the error message
  return err.message;
};

export const displayBaseFieldValue = (
  baseFieldConfig: CreditAgreement.DataTapeField.BaseConfig,
) => {
  const { type } = baseFieldConfig;
  switch (type) {
    case 'currency':
    case 'optionalCurrency':
      return (value: any) =>
        _.isNumber(value)
          ? formatCurrencyAmount(value, baseFieldConfig.currencyCode)
          : '-';
    case 'boolean':
    case 'optionalBoolean':
      return (value: any) => (_.isBoolean(value) ? String(value) : '-');
    case 'date':
    case 'optionalDate':
      return (value: any) =>
        value
          ? DateTime.fromFormat(
              value,
              baseFieldConfig.externalDateFormat,
            ).toLocaleString(DateTime.DATE_FULL)
          : '-';
    case 'dateTime':
    case 'optionalDateTime':
      return (value: any) =>
        value
          ? DateTime.fromFormat(value, baseFieldConfig.externalDateTimeFormat)
              .setZone(baseFieldConfig.defaultTz)
              .toLocaleString(DateTime.DATETIME_FULL)
          : '-';
    case 'percentage':
    case 'optionalPercentage':
      return (value: any) =>
        _.isNumber(value) ? numberToPercentageLabel(value) : '-';
    case 'float':
    case 'integer':
    case 'optionalFloat':
    case 'optionalInteger':
      return (value: any) => (_.isNumber(value) ? value : '-');

    case 'string':
    case 'optionalString':
    default:
      return (value: any) => (_.isString(value) ? value : '-');
  }
};

export const formatBaseFieldValue = (
  value: any,
  baseFieldType: CreditAgreement.DataTapeField.Type,
) => {
  if (value === null || value === undefined || value === 'undefined') {
    return '-';
  }
  if (baseFieldType === 'date' || baseFieldType === 'optionalDate') {
    return commonDate.dateToHumanDateString(DateTime.fromISO(value).toJSDate());
  }
  if (baseFieldType === 'currency' || baseFieldType === 'optionalCurrency') {
    return commonUtils.numberToCurrencyWithSymbol({
      number: Number(value),
    });
  }
  if (
    baseFieldType === 'percentage' ||
    baseFieldType === 'optionalPercentage'
  ) {
    return numberToPercentageLabel(Number(value));
  }
  if (baseFieldType === 'float' || baseFieldType === 'optionalFloat') {
    return Number(value).toFixed(2);
  }

  return String(value);
};

export const scrollToTopOfElement = (elementId: string) => {
  document.getElementById(elementId)?.scroll({ top: 0 });
};

export const getHumanReadableDateRange = ({
  startDate,
  endDate,
}: {
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
}) => {
  const startDateObj = DateTime.fromISO(startDate);
  const endDateObj = DateTime.fromISO(endDate);

  if (startDateObj.year !== endDateObj.year) {
    return `${startDateObj.toLocaleString(
      DateTime.DATE_FULL,
    )} - ${endDateObj.toLocaleString(DateTime.DATE_FULL)}`;
  }

  if (startDateObj.month !== endDateObj.month) {
    return `${startDateObj.toFormat('MMMM d')} - ${endDateObj.toFormat(
      'MMMM d, yyyy',
    )}`;
  }

  return `${startDateObj.toFormat('MMMM d')} - ${endDateObj.toFormat(
    'd, yyyy',
  )}`;
};

// @jendevelops TODO: revisit definition of "has borrowing base" for multi-loan product facilities
export const hasBorrowingBaseMechanics = ({
  calcAgentMechanics: {
    loanProductMechanics,
    multiLoanProductConcentrationLimits,
  },
}: CreditAgreement.DomainEntity.BorrowingBaseMechanicsWithEntities): boolean => {
  return (
    loanProductMechanics.length > 0 ||
    multiLoanProductConcentrationLimits.length > 0
  );
};

// While we expect the provided input to be a proper UUID format,
// it's possible this isn't always the case, especially as we may
// use this for other inputs (e.g, external data).
// Thus, factor in various string shortening mechanisms here
export const truncateUuid = (uuid: string): string => {
  if (uuid.length <= 12) {
    return uuid.toUpperCase();
  }
  return `...${uuid.slice(-12).toUpperCase()}`;
};
